import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { POTENTIAL_CHANGE_ORDERS_SECTION_CONFIG } from './sections.config';

interface PotentialChangeOrdersSectionProps {
  magicReportId: string;
}

const COMMENT_FIELD = 'PotentialChangeOrdersComment';
const PLACEHOLDER = 'XXXXXXX';
export const DEFAULT_POTENTIAL_CHANGE_ORDERS_COMMENT =
  'Currently there are XXXXXXX potential change orders under review, after the items through Change Order #XXXXXXX were removed. The estimated range of cost for the items under review is approximately $XXXXXXX, and the more significant items currently under consideration are: XXXXXXX, XXXXXXX, XXXXXXX, and other misc items. None of the items would appear to have a negative effect on the value of the project.';

const PotentialChangeOrdersSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ handleFieldChange, getField, onBook }) => {
  // Comment carries forward month-to-month on the backend, so it's only ever auto-populated
  // once, the first time this field has no value at all (mirrors ConstructionMeetingMinutesComment).
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    if (!getField(COMMENT_FIELD)?.value) {
      handleFieldChange(COMMENT_FIELD, { value: DEFAULT_POTENTIAL_CHANGE_ORDERS_COMMENT });
    }
  }, [getField, handleFieldChange]);

  const handleRefreshComment = () => {
    handleFieldChange(COMMENT_FIELD, { value: DEFAULT_POTENTIAL_CHANGE_ORDERS_COMMENT });
  };

  const layout = useMemo(
    () => ({
      singleColumn: [
        {
          fieldName: COMMENT_FIELD,
          type: 'text-with-refresh' as const,
          label: '',
          errorIfContains: PLACEHOLDER,
          onRefresh: handleRefreshComment,
        },
      ],
    }),
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />
    </Box>
  );
};

export const PotentialChangeOrdersSection: React.FC<PotentialChangeOrdersSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={POTENTIAL_CHANGE_ORDERS_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <PotentialChangeOrdersSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
